"use client";

import { useState } from "react";
import { ShoppingCart, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

// Helper function to parse dates and times from API
function parseDateTime(dateStr?: string, timeStr?: string): { date: string; time: string } {
  try {
    if (!dateStr && !timeStr) {
      return { date: 'TBD', time: '' };
    }

    let formattedDate = 'TBD';
    let formattedTime = '';

    if (dateStr && typeof dateStr === 'string' && dateStr.trim().length > 0) {
      try {
        if (dateStr.length >= 8 && /^\d{8}/.test(dateStr)) {
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          
          if (parseInt(month) >= 1 && parseInt(month) <= 12 && parseInt(day) >= 1 && parseInt(day) <= 31) {
            const dateObj = new Date(`${year}-${month}-${day}T00:00:00Z`);
            if (!isNaN(dateObj.getTime())) {
              formattedDate = dateObj.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: '2-digit' 
              });
            }
          }
        } else {
          const dateObj = new Date(dateStr);
          if (!isNaN(dateObj.getTime())) {
            formattedDate = dateObj.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'short', 
              day: '2-digit' 
            });
          } else {
            formattedDate = dateStr;
          }
        }
      } catch (e) {
        formattedDate = dateStr || 'TBD';
      }
    }

    if (timeStr && typeof timeStr === 'string' && timeStr.trim().length >= 4) {
      try {
        const timeClean = timeStr.trim();
        if (/^\d{4}/.test(timeClean)) {
          const hours = timeClean.substring(0, 2);
          const minutes = timeClean.substring(2, 4);
          
          if (parseInt(hours) >= 0 && parseInt(hours) < 24 && parseInt(minutes) >= 0 && parseInt(minutes) < 60) {
            formattedTime = `${hours}:${minutes}`;
          }
        }
      } catch (e) {
        // ignore
      }
    }

    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    return { date: 'TBD', time: '' };
  }
}

export function FloatingCart() {
  const { cart, removeFromCart, clearCart, cartTotal, isCheckout, setIsCheckout, hasSearched } = useCart();
  const [showCart, setShowCart] = useState(false);

  const animationStyles = `
    @keyframes cartPulse {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
      }
      25% {
        transform: scale(1.1);
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5);
      }
      50% {
        transform: scale(1.05);
        box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.3);
      }
      75% {
        transform: scale(1.1);
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5);
      }
    }
    
    @keyframes cartGlow {
      0%, 100% {
        filter: drop-shadow(0 0 5px rgba(59, 130, 246, 0.4));
      }
      50% {
        filter: drop-shadow(0 0 15px rgba(59, 130, 246, 0.8));
      }
    }
    
    .animate-cart-pulse {
      animation: cartPulse 2s infinite;
    }
    
    .animate-cart-glow {
      animation: cartGlow 2s infinite;
    }
  `;

  return (
    <>
      <style>{animationStyles}</style>
      
      {/* Show cart when items are in it OR search has been performed */}
      {(cart.length > 0 || hasSearched) && (
      <>
      {/* Floating Cart Icon - Bottom Left */}
      <div className="fixed left-4 bottom-4 z-40">
        <div className="relative">
          <button 
            onClick={() => !isCheckout && setShowCart(!showCart)}
            disabled={isCheckout}
            className={`flex items-center justify-center w-16 h-16 rounded-full border-2 border-primary bg-card shadow-lg hover:shadow-2xl transition-all ${
              cart.length > 0 && !isCheckout ? 'animate-cart-pulse animate-cart-glow' : ''
            } ${isCheckout ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isCheckout ? "Complete your booking first" : "Shopping cart"}
            aria-label="Shopping cart"
          >
            <ShoppingCart className="w-7 h-7 text-primary" />
          </button>
          
          {/* Item Count Badge */}
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 px-2 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold min-w-[24px] text-center">
              {cart.length}
            </span>
          )}

          {/* Cart Dropdown Modal */}
          {showCart && (
            <div className="absolute left-20 -top-[420px] w-96 max-w-[calc(100vw-120px)] bg-card border border-border/50 rounded-lg shadow-lg z-50">
              <div className="p-4 border-b border-border/30 flex justify-between items-center">
                <h3 className="font-bold text-lg">Your Bookings</h3>
                <button 
                  onClick={() => setShowCart(false)}
                  className="text-muted-foreground hover:text-foreground"
                  title="Close cart"
                  aria-label="Close cart"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {cart.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <p>No bookings added yet</p>
                  <p className="text-xs mt-2">Click on a service to add it to your cart</p>
                </div>
              ) : (
                <>
                  <div className="max-h-96 overflow-y-auto space-y-3 p-4">
                    {cart.map((item) => {
                      const boardDateTime = parseDateTime(item.board_date, item.board_time);
                      
                      return (
                        <div key={item.cartId} className="p-3 bg-muted/30 rounded-lg border border-border/30">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className="font-semibold text-sm">{item.carrier_name || item.carrier}</p>
                              <p className="text-xs text-muted-foreground">#{item.service_number?.trim()}</p>
                            </div>
                            <button
                              onClick={() => removeFromCart(item.cartId)}
                              className="text-muted-foreground hover:text-destructive ml-2"
                              title="Remove from cart"
                              aria-label="Remove from cart"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs mb-1">
                            <span className="text-muted-foreground">{boardDateTime.date}</span>
                            {boardDateTime.time && <span className="ml-1 font-semibold">{boardDateTime.time}</span>}
                          </p>
                          <p className="text-xs text-primary font-semibold">
                            R {(item.fare || 0).toFixed(2)}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Cart Footer */}
                  <div className="p-4 border-t border-border/30 space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-border/30">
                      <span className="font-semibold">Total:</span>
                      <span className="font-bold text-lg text-primary">R {cartTotal.toFixed(2)}</span>
                    </div>
                    {!isCheckout && (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleClearCart}
                          className="flex-1"
                        >
                          Clear All
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => {
                            setIsCheckout(true);
                            setShowCart(false);
                          }}
                          className="flex-1"
                        >
                          Proceed to Booking
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </>
  );

  function handleClearCart() {
    clearCart();
    setShowCart(false);
  }
}
